import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Spinner } from "reactstrap";
import "./datagrid.scss";
import DataGridPagination from "./DataGridPagination";
import EmptyStateVector from "../../assets/images/empty-state.svg";

interface Pagination {
  results?: object[];
  page?: number;
  limit?: number;
  totalPages?: number;
  totalResults?: number;
  startIndex?: number;
  endIndex?: number;
}

interface GridColumn {
  id?: string | number;
  name: React.ReactNode;
  selector: Function;
  sortable?: boolean;
  wrap?: boolean;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  center?: boolean;
  right?: boolean;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
}

interface IDataGrid {
  columns: GridColumn[];
  data: Pagination;
  FilterSubHeader?: boolean;
  planData?: any;
  viewData?: any;
  filterByDate?: any;
  filterData?: any;
  setFilterData?: any;
  noTableHead?: boolean;
  subHeaderComponent?: React.ReactNode;
  selectableRows?: boolean;
  onSelectedRowsChange?: Function;
  pagination?: boolean;
  fixedHeader?: boolean;
  noDataTitle?: string;
  noDataSubtitle?: string;
  noDataFilteredTitle?: string;
  noDataFilteredSubtitle?: string;
  isFilterApplied?: boolean;
  loading?: boolean;
}

type InternalGridColumn = GridColumn & { id: string };

const getColumnId = (column: GridColumn, index: number): string => String(column.id ?? index + 1);

const getWidthValue = (value: unknown): number | null => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = Number.parseFloat(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  return null;
};

const measureIntrinsicWidth = (node: HTMLElement): number => {
  const clone = node.cloneNode(true) as HTMLElement;

  clone.style.position = "absolute";
  clone.style.visibility = "hidden";
  clone.style.pointerEvents = "none";
  clone.style.left = "-9999px";
  clone.style.top = "-9999px";
  clone.style.width = "max-content";
  clone.style.minWidth = "0";
  clone.style.maxWidth = "none";
  clone.style.whiteSpace = "nowrap";

  document.body.appendChild(clone);
  const measuredWidth = Math.ceil(clone.getBoundingClientRect().width || clone.scrollWidth);
  document.body.removeChild(clone);

  return measuredWidth;
};

const getCellAlignment = (column: GridColumn) => {
  if (column.right) {
    return "right";
  }

  if (column.center) {
    return "center";
  }

  return "left";
};

const isActionColumn = (columnName: React.ReactNode): boolean =>
  typeof columnName === "string" && /^actions?$/i.test(columnName.trim());

const getContentAlignmentStyle = (column: GridColumn): React.CSSProperties => {
  const alignment = getCellAlignment(column);

  return {
    justifyContent:
      alignment === "right" ? "flex-end" : alignment === "center" ? "center" : "flex-start",
    textAlign: alignment,
  };
};

const getColumnStyle = (
  column: GridColumn,
  measuredWidth: string | undefined,
  isHeader = false,
): React.CSSProperties => {
  const alignment = getCellAlignment(column);
  const baseStyle: React.CSSProperties = {
    minWidth: column.minWidth ?? column.width ?? measuredWidth ?? "100px",
    maxWidth: column.maxWidth ?? column.width ?? measuredWidth,
    width: column.width ?? measuredWidth,
    textAlign: alignment,
    justifyContent:
      alignment === "right" ? "flex-end" : alignment === "center" ? "center" : "flex-start",
  };

  if (!isHeader && alignment === "right") {
    baseStyle.paddingRight = "1.25rem";
  }

  if (isHeader && alignment === "right") {
    baseStyle.paddingRight = "1.25rem";
  }

  return {
    ...baseStyle,
    ...(isHeader ? column.headerStyle : column.style),
  };
};

const DataGrid: React.FC<IDataGrid> = ({
  columns,
  data,
  FilterSubHeader,
  subHeaderComponent,
  selectableRows = false,
  onSelectedRowsChange,
  pagination = true,
  fixedHeader = true,
  noDataTitle,
  noDataSubtitle,
  noDataFilteredTitle,
  noDataFilteredSubtitle,
  filterData,
  isFilterApplied,
  noTableHead = false,
  loading = false,
}) => {
  const gridRef = React.useRef<HTMLDivElement | null>(null);
  const tableHeadRef = React.useRef<HTMLTableSectionElement | null>(null);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, string>>({});
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [hasScrolledBody, setHasScrolledBody] = React.useState(false);
  const [headerHeight, setHeaderHeight] = React.useState(0);
  const rows = React.useMemo(
    () => (Array.isArray(data?.results) ? data.results : []),
    [data?.results],
  );

  const handleGridScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setHasScrolledBody(event.currentTarget.scrollTop > 0);
  }, []);

  const hasAppliedFilters = React.useMemo(() => {
    if (!filterData || typeof filterData !== "object") {
      return false;
    }

    return Object.values(filterData).some((value: any) => {
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === "object") {
        return Object.keys(value).length > 0;
      }
      return Boolean(value);
    });
  }, [filterData]);

  const showFilteredEmptyState = isFilterApplied ?? hasAppliedFilters;

  const activeNoDataTitle = showFilteredEmptyState
    ? noDataFilteredTitle || "No matching records found"
    : noDataTitle || "No records found";

  const activeNoDataSubtitle = showFilteredEmptyState
    ? noDataFilteredSubtitle || "Try changing or clearing filters and search criteria."
    : noDataSubtitle || "There are no records to display right now.";

  const noDataState = (
    <div className='data-grid-empty-state'>
      <img src={EmptyStateVector} alt='No records' className='data-grid-empty-state-image' />
      <p className='data-grid-empty-state-title'>{activeNoDataTitle}</p>
      <p className='data-grid-empty-state-subtitle'>{activeNoDataSubtitle}</p>
    </div>
  );

  const loadingState = (
    <div className='data-grid-loading-state'>
      <Spinner color='primary' />
      <p className='data-grid-loading-state-title'>Loading records...</p>
    </div>
  );

  const normalizedColumns = React.useMemo<InternalGridColumn[]>(
    () =>
      columns.map((column, index) => {
        const columnId = getColumnId(column, index);
        const measuredWidth = columnWidths[columnId];
        const isActionsColumn = isActionColumn(column.name);
        const configuredActionWidth = Math.max(
          getWidthValue(column.width) ?? 0,
          getWidthValue(column.minWidth) ?? 0,
          getWidthValue(column.maxWidth) ?? 0,
        );
        const actionWidth = isActionsColumn
          ? `${Math.max(configuredActionWidth, 110)}px`
          : undefined;

        return {
          ...column,
          id: columnId,
          minWidth: isActionsColumn
            ? actionWidth
            : column.minWidth ?? column.width ?? measuredWidth ?? "100px",
          maxWidth: isActionsColumn
            ? actionWidth
            : column.maxWidth ?? column.width ?? measuredWidth,
          width: isActionsColumn ? actionWidth : column.width ?? measuredWidth,
          center: isActionsColumn ? false : column.center,
          right: isActionsColumn ? true : column.right,
        };
      }),
    [columnWidths, columns],
  );

  const tableColumns = React.useMemo<ColumnDef<any>[]>(
    () => [
      ...(selectableRows
        ? [
            {
              id: "__select__",
              header: ({ table }: any) => (
                <input
                  type='checkbox'
                  className='form-check-input m-0'
                  checked={table.getIsAllRowsSelected()}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = table.getIsSomeRowsSelected();
                    }
                  }}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />
              ),
              cell: ({ row }: any) => (
                <input
                  type='checkbox'
                  className='form-check-input m-0'
                  checked={row.getIsSelected()}
                  disabled={!row.getCanSelect()}
                  onChange={row.getToggleSelectedHandler()}
                />
              ),
              enableSorting: false,
              size: 48,
              minSize: 48,
              maxSize: 48,
              meta: {
                className: "dataGridSelectionColumn",
                style: {
                  minWidth: "48px",
                  maxWidth: "48px",
                  width: "48px",
                  textAlign: "center" as const,
                },
              },
            } as ColumnDef<any>,
          ]
        : []),
      ...normalizedColumns.map(
        (column): ColumnDef<any> => ({
          id: column.id,
          accessorFn: (row) => row,
          enableSorting: Boolean(column.sortable),
          header: () => column.name,
          cell: ({ row }) => column.selector(row.original),
          size: getWidthValue(column.width) ?? undefined,
          minSize: getWidthValue(column.minWidth) ?? undefined,
          maxSize: getWidthValue(column.maxWidth) ?? undefined,
          meta: {
            column,
          },
        }),
      ),
    ],
    [normalizedColumns, selectableRows],
  );

  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    state: {
      rowSelection,
      sorting,
    },
    enableRowSelection: selectableRows,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row, index) => String((row as any)?.id ?? index),
  });

  const selectedRows = React.useMemo(
    () => table.getSelectedRowModel().rows.map((row) => row.original),
    [rowSelection, rows, table],
  );

  React.useEffect(() => {
    if (!onSelectedRowsChange) {
      return;
    }

    onSelectedRowsChange({ selectedRows });
  }, [onSelectedRowsChange, selectedRows]);

  React.useEffect(() => {
    const validRowIds = new Set(rows.map((row: any, index) => String(row?.id ?? index)));

    setRowSelection((previousSelection) => {
      const nextSelection = Object.fromEntries(
        Object.entries(previousSelection).filter(([rowId]) => validRowIds.has(rowId)),
      );

      return Object.keys(nextSelection).length === Object.keys(previousSelection).length
        ? previousSelection
        : nextSelection;
    });
  }, [rows]);

  React.useEffect(() => {
    if (!rows.length) {
      setHasScrolledBody(false);
    }
  }, [rows.length]);

  React.useLayoutEffect(() => {
    const headNode = tableHeadRef.current;

    if (!headNode || noTableHead) {
      setHeaderHeight(0);
      return;
    }

    const updateHeaderHeight = () => {
      setHeaderHeight(Math.ceil(headNode.getBoundingClientRect().height));
    };

    updateHeaderHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeaderHeight);

      return () => {
        window.removeEventListener("resize", updateHeaderHeight);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      updateHeaderHeight();
    });

    resizeObserver.observe(headNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [columns, noTableHead, rows.length, selectableRows]);

  React.useLayoutEffect(() => {
    const container = gridRef.current;
    if (!container) {
      return;
    }

    const nextWidths: Record<string, string> = {};

    normalizedColumns.forEach((column) => {
      if (column.width || column.minWidth || column.maxWidth) {
        return;
      }

      const nodes = container.querySelectorAll<HTMLElement>(`[data-column-id="${column.id}"]`);
      if (!nodes.length) {
        return;
      }

      let maxWidth = 0;

      nodes.forEach((node) => {
        maxWidth = Math.max(maxWidth, measureIntrinsicWidth(node));
      });

      if (maxWidth > 0) {
        nextWidths[column.id] = `${Math.ceil(maxWidth + 48)}px`;
      }
    });

    setColumnWidths((previousWidths) => {
      const previousKeys = Object.keys(previousWidths);
      const nextKeys = Object.keys(nextWidths);

      if (
        previousKeys.length === nextKeys.length &&
        nextKeys.every((key) => previousWidths[key] === nextWidths[key])
      ) {
        return previousWidths;
      }

      return nextWidths;
    });
  }, [normalizedColumns, rows]);

  return (
    <div ref={gridRef} className='dataGridListMain'>
      {FilterSubHeader && subHeaderComponent ? (
        <div className='dataGridSubHeader'>{subHeaderComponent}</div>
      ) : null}

      <div
        className={`dataTableCover${fixedHeader ? " dataTableCover--fixedHeader" : ""}${
          hasScrolledBody ? " dataTableCover--scrolled" : ""
        }`}
      >
        {fixedHeader && !noTableHead && hasScrolledBody ? (
          <div className='dataGridHeaderShadow' style={{ top: `${headerHeight}px` }} aria-hidden='true' />
        ) : null}
        {loading ? <div className='dataGridLoadingOverlay'>{loadingState}</div> : null}
        <div className='dataGridScroll' onScroll={handleGridScroll}>
          {rows.length > 0 || loading ? (
            <table className='dataGridTable'>
              {!noTableHead ? (
                <thead ref={tableHeadRef} className='dataGridTableHead'>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className='dataGridTableHeadRow'>
                      {headerGroup.headers.map((header) => {
                        const columnMeta = header.column.columnDef.meta as
                          | {
                              column?: InternalGridColumn;
                              className?: string;
                              style?: React.CSSProperties;
                            }
                          | undefined;
                        const column = columnMeta?.column;
                        const sortState = header.column.getIsSorted();
                        const headerStyle = column
                          ? getColumnStyle(column, columnWidths[column.id], true)
                          : columnMeta?.style;
                        const contentStyle = column ? getContentAlignmentStyle(column) : undefined;

                        return (
                          <th
                            key={header.id}
                            className={`dataGridTableCol ${columnMeta?.className || ""}`.trim()}
                            style={headerStyle}
                          >
                            {header.isPlaceholder ? null : header.column.getCanSort() ? (
                              <button
                                type='button'
                                className={`dataGridSortButton ${
                                  sortState === "asc"
                                    ? "sortAsc"
                                    : sortState === "desc"
                                    ? "sortDesc"
                                    : ""
                                }`.trim()}
                                onClick={header.column.getToggleSortingHandler()}
                                style={contentStyle}
                              >
                                <span
                                  className='dataGridHeaderContent'
                                  data-column-id={column?.id || header.column.id}
                                  style={contentStyle}
                                >
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                </span>
                                <span className='sortIconMain d-flex align-items-center flex-column'>
                                  <span className='sortIconUp'></span>
                                  <span className='sortIconDown'></span>
                                </span>
                              </button>
                            ) : (
                              <div
                                className='dataGridHeaderContent'
                                data-column-id={column?.id || header.column.id}
                                style={contentStyle}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
              ) : null}
              <tbody className='dataGridTableBody'>
                {!loading &&
                  table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className='dataGridTableRow'>
                    {row.getVisibleCells().map((cell) => {
                      const columnMeta = cell.column.columnDef.meta as
                        | {
                            column?: InternalGridColumn;
                            className?: string;
                            style?: React.CSSProperties;
                          }
                        | undefined;
                      const column = columnMeta?.column;
                      const cellStyle = column
                        ? getColumnStyle(column, columnWidths[column.id])
                        : columnMeta?.style;
                      const contentStyle = column ? getContentAlignmentStyle(column) : undefined;

                      return (
                        <td
                          key={cell.id}
                          className={`dataGridTableCell ${columnMeta?.className || ""}`.trim()}
                          style={cellStyle}
                        >
                          <div
                            className='dataGridCellContent'
                            data-column-id={column?.id || cell.column.id}
                            style={contentStyle}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            noDataState
          )}
        </div>
      </div>

      {pagination ? (
        <div className='dataGridPaginationWrap'>
          <DataGridPagination
            totalPages={data.totalPages}
            totalResults={data.totalResults}
            page={data.page}
            startIndex={data.startIndex}
            endIndex={data.endIndex}
          />
        </div>
      ) : null}
    </div>
  );
};

export default DataGrid;
